import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSessionStorage, useUnmount } from 'usehooks-ts';
import { CommanderThunkDispatch, WithGuideLink, WithReservationId } from '@mews-commander/types';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { setProducts } from '@mews-commander/legacy-entities/src/ducks/products';
import {
    useEnterpriseProductsQuery,
    useGroupReservationsQuery,
    useInvalidateReservationGroupAllQuery,
    useReservationGroupOrderItemsQuery,
    useUpdateReservationsQuery,
} from '@mews-commander/core/entities';
import { processReservationResult } from '@mews-commander/legacy-entities/src/actions/reservations';
import { OrderItem, Reservation, ServiceOrderState, useRepositoryQuery } from '@mews-commander/data-access';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { keyBy } from '@mews/fp';
import { omit } from '@mews/std';
import { setBills } from '@mews-commander/legacy-entities/src/ducks/bills';
import { initializeReservationModuleOld, initializeReservationModuleNew } from '../../actions';
import { ModalBody } from '../../../modalManager/ModalBody';
import { getCheckedReservationIds, getCheckedReservations } from '../../selectors';
import { subscribeResourceUpdates, unsubscribeResourceUpdates } from '../../../../core/websockets/eventHandlers/spaces';
import { useHasNavigatedBack } from '../../../../core/history';
import { useLoaderVisible } from '../../../../core/pageLoader';
import { trackReservationModuleEvent } from '../../actions/tracking';
import ReservationModule from './ReservationModule';
import { FetchingContextProvider } from './context';

interface Props {
    groupId: string;
}
const createReservationModuleInitTracker = (dispatch: CommanderThunkDispatch, groupId: string) => () => {
    const start = window.performance.now();

    let initialTime: number | null = null;
    let orderItemQueryTime: number | null = null;
    let loadingPromiseTime: number | null = null;
    let groupSize: number | null = null;
    let initializationSubmitted = false;

    const markInitial = () => {
        initialTime = window.performance.now() - start;
        sendObservabilityData();
    };
    const markOrderItemQuery = () => {
        orderItemQueryTime = window.performance.now() - start;
        sendObservabilityData();
    };
    const markLoadingPromise = () => {
        loadingPromiseTime = window.performance.now() - start;
        sendObservabilityData();
    };
    const markGroupSize = (size: number) => {
        groupSize = size;
        sendObservabilityData();
    };
    const sendObservabilityData = () => {
        if (initializationSubmitted || !initialTime || !orderItemQueryTime || !loadingPromiseTime || !groupSize) return;
        const eventValue = {
            totalTime: Math.max(orderItemQueryTime, loadingPromiseTime),
            initialTime,
            groupId,
            groupSize,
        };
        initializationSubmitted = true;

        dispatch(
            trackReservationModuleEvent({
                eventAction: 'reservationModuleInitialized',
                eventValue: JSON.stringify(eventValue),
            })
        );
    };
    return {
        markInitial,
        markOrderItemQuery,
        markLoadingPromise,
        markGroupSize,
    };
};

export const ReservationDetailContainer = ({
    reservationId,
    guideLink,
    groupId,
}: WithReservationId & WithGuideLink & Props) => {
    const dispatch: CommanderThunkDispatch = useDispatch();

    const [fetching, setFetching] = useState(false);

    const enterpriseId = useCurrentEnterpriseId();

    const hasNavigatedBack = useHasNavigatedBack();
    const [storedReservationIds, storeReservationIds] = useSessionStorage(`RM-group-reservations-${groupId}`, [
        reservationId,
    ]);
    const reservationIds = useCommanderSelector((state) => getCheckedReservationIds(state, { scope: groupId }));
    const { isActive, areQueriesFetching } = useInitializeModule(
        groupId,
        hasNavigatedBack ? storedReservationIds : [reservationId]
    );

    const isWindowFetching = useLoaderVisible();

    const isFetching = isWindowFetching || fetching || areQueriesFetching;
    useEffect(() => {
        dispatch(subscribeResourceUpdates());
        return () => {
            dispatch(unsubscribeResourceUpdates());
        };
    }, [dispatch]);

    useSynchonizeReduxProducts();

    useUnmount(() => {
        storeReservationIds(reservationIds);
    });

    return (
        <FetchingContextProvider value={{ setFetching }}>
            <ModalBody isActive={isActive} isFetching={isFetching}>
                <ReservationModule
                    enterpriseId={enterpriseId}
                    groupId={groupId}
                    reservationId={reservationId}
                    guideLink={guideLink}
                />
            </ModalBody>
        </FetchingContextProvider>
    );
};
const getReservationsWeakSet = () => new WeakSet<Reservation[]>();
const useInitializeModule = (groupId: string, reservationIds: string[]) => {
    const [isActive, setActive] = useState(false);

    const isReservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);
    const dispatch: CommanderThunkDispatch = useDispatch();
    const [observability] = useState(createReservationModuleInitTracker(dispatch, groupId));

    const reservationGroupQuery = useGroupReservationsQuery(groupId, {
        enabled: isReservationModuleReactQueryEnabled,
    });
    const nonCanceledOnly = useCommanderSelector(
        (state) =>
            // Using negation so we wont refetch on case we unselect all reservations
            !getCheckedReservations(state, { scope: groupId }).some(
                (r) => r.serviceOrderState === ServiceOrderState.Canceled
            )
    );
    const orderItemQuery = useReservationGroupOrderItemsQuery(groupId, {
        enabled: isReservationModuleReactQueryEnabled,
        nonCanceledOnly,
    });

    const updateReservationQueries = useUpdateReservationsQuery(groupId);
    const [skipUpdates] = useState(getReservationsWeakSet);
    // Skip updates if the new data are coming from the previous update
    // This happen due to reactivity of the query, as the updated data are again passed to the effect.
    useEffect(() => {
        const newestReservations =
            reservationGroupQuery.dataUpdatedAt > orderItemQuery.dataUpdatedAt
                ? reservationGroupQuery.data?.reservations
                : orderItemQuery.data?.reservations;

        // This is the fallback of manually updating the query state.
        // Another update is created from the sync, thus we need to filter those updates to avoid infinite rerendering.
        if (!newestReservations || skipUpdates.has(newestReservations)) return;

        const keyedReservations: Record<string, Reservation> = keyBy('id', newestReservations);
        updateReservationQueries(
            (reservationResponse) => {
                if (!reservationResponse || !reservationResponse.reservations) return undefined;

                if (reservationResponse.reservations === newestReservations) {
                    skipUpdates.add(reservationResponse.reservations);
                    return undefined;
                }

                // We might get different subset of reservations, namely we can omit noncanceled reservations
                // if we are not working with them.
                const updatedReservations = reservationResponse.reservations.map((r) => keyedReservations[r.id] ?? r);
                skipUpdates.add(updatedReservations);
                return {
                    ...reservationResponse,
                    reservations: updatedReservations,
                };
            },
            // Omit updating stale queries. Update on stale queries makes them not stale, disregarding invalidation.
            // Refetch is preferred in this case.
            { stale: false }
        );
    }, [
        orderItemQuery.data?.reservations,
        orderItemQuery.dataUpdatedAt,
        reservationGroupQuery.data?.reservations,
        reservationGroupQuery.dataUpdatedAt,
        skipUpdates,
        updateReservationQueries,
    ]);
    // Synchronize reservation data with the redux store
    // Query is invalidated instead of the refetch, thus the redxu is at least in some place ditched
    useEffect(() => {
        if (reservationGroupQuery.data && isReservationModuleReactQueryEnabled) {
            observability.markInitial();
            observability.markGroupSize(reservationGroupQuery.data.reservations?.length ?? 0);
            dispatch(processReservationResult(reservationGroupQuery.data));
        }
    }, [dispatch, reservationGroupQuery.data, isReservationModuleReactQueryEnabled, observability]);

    useEffect(() => {
        if (orderItemQuery.data && isReservationModuleReactQueryEnabled) {
            observability.markOrderItemQuery();
            // Reservations are synced with reservationGroupQuery.data
            dispatch(processReservationResult(omit('reservations', orderItemQuery.data)));
        }
    }, [dispatch, orderItemQuery.data, isReservationModuleReactQueryEnabled, observability]);

    useEffect(() => {
        const reservationData = reservationGroupQuery.data;
        if (!isActive) {
            if (isReservationModuleReactQueryEnabled) {
                if (reservationData) {
                    const load = async () => {
                        await dispatch(
                            initializeReservationModuleNew({
                                groupId,
                                reservationIds,
                                reservationData,
                            })
                        );
                        observability.markLoadingPromise();
                    };
                    void load();
                    setActive(true);
                }
            } else {
                void dispatch(
                    initializeReservationModuleOld({
                        groupId,
                        reservationIds,
                    })
                );
                setActive(true);
            }
        }
    }, [
        dispatch,
        groupId,
        isActive,
        isReservationModuleReactQueryEnabled,
        observability,
        reservationGroupQuery.data,
        reservationIds,
    ]);

    useFetchBills(orderItemQuery.data?.orderItems);

    const invalidateAllData = useInvalidateReservationGroupAllQuery(groupId);
    useUnmount(() => {
        window.setTimeout(async () => invalidateAllData(), 0);
    });
    return { isActive, areQueriesFetching: reservationGroupQuery.isFetching };
};
const useSynchonizeReduxProducts = () => {
    const dispatch = useCommanderThunkDispatch();
    const productsQuery = useEnterpriseProductsQuery();
    useEffect(() => {
        // Sync redux store because of order items and the overall redux connection
        // @ts-expect-error
        if (productsQuery.data?.products) dispatch(setProducts(productsQuery.data.products));
    }, [dispatch, productsQuery.data?.products]);
};

const useFetchBills = (orderItems?: OrderItem[] | null) => {
    const dispatch = useCommanderThunkDispatch();

    const billIds = useMemo(() => {
        if (!orderItems) return [];
        const billIdsSet = new Set<string>();
        orderItems.forEach((orderItem) => {
            if (orderItem.billId) {
                if (orderItem.billId !== orderItem.ownerId) {
                    billIdsSet.add(orderItem.billId);
                }
            }
        });
        return Array.from(billIdsSet);
    }, [orderItems]);

    const { data } = useRepositoryQuery(
        'bills',
        'getAll',
        {
            billIds,
        },
        {
            enabled: billIds.length > 0,
        }
    );

    // TODO: Remove this once we have migrated out of legacy redux entities.
    useEffect(() => {
        if (data?.bills?.length) {
            dispatch(setBills(data.bills));
        }
    }, [dispatch, data?.bills]);
};
